import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import type { Coach } from "../../api/types";
import { useAuth } from "../../auth/AuthContext";
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  FormError,
  Loading,
  Modal,
  PageHead,
  TextArea,
  TextField,
  useToast,
} from "../../components/ui";
import { useAsync } from "../../lib/useAsync";
import { Link } from "../../router/router";
import { t } from "../../i18n";

export function CoachesPage({ orgId }: { orgId: string }) {
  const { can } = useAuth();
  const toast = useToast();
  const canManage = can("coaches.manage");
  const [showInactive, setShowInactive] = useState(false);
  const list = useAsync(
    () => api.get<Coach[]>(`/orgs/${orgId}/coaches`, { include_inactive: showInactive }),
    [orgId, showInactive],
  );
  const [editing, setEditing] = useState<Coach | "new" | null>(null);

  async function toggleActive(c: Coach) {
    try {
      await api.patch(`/orgs/${orgId}/coaches/${c.id}`, { is_active: !c.is_active });
      toast(c.is_active ? t("{name} foi desativado(a).", { name: c.full_name }) : t("{name} foi reativado(a).", { name: c.full_name }));
      list.reload();
    } catch (err) {
      toast((err as ApiError).message, "error");
    }
  }

  return (
    <>
      <PageHead
        title={t("Treinadores")}
        subtitle={t("Quem prescreve e acompanha os treinos. Cada treinador acessa apenas os atletas atribuídos a ele.")}
        actions={
          canManage && (
            <Button variant="primary" onClick={() => setEditing("new")}>
              {t("Cadastrar treinador")}
            </Button>
          )
        }
      />
      {canManage && (
        <label className="checkbox" style={{ marginBottom: 12 }}>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          {t("Mostrar desativados")}
        </label>
      )}
      {list.loading && !list.data && <Loading />}
      {list.error && <ErrorState error={list.error} onRetry={list.reload} />}
      {list.data && list.data.length === 0 && (
        <div className="panel">
          <EmptyState
            title={t("Nenhum treinador cadastrado")}
            action={canManage && <Button variant="primary" onClick={() => setEditing("new")}>{t("Cadastrar treinador")}</Button>}
          >
            {t("O treinador recebe um convite por e-mail para criar a senha.")}
          </EmptyState>
        </div>
      )}
      {list.data && list.data.length > 0 && (
        <div className="table-wrap">
          <table className="data responsive">
            <thead>
              <tr>
                <th>{t("Treinador")}</th>
                <th>{t("Telefone")}</th>
                <th className="right">{t("Atletas")}</th>
                <th>{t("Acesso")}</th>
                {canManage && <th className="right">{t("Ações")}</th>}
              </tr>
            </thead>
            <tbody>
              {list.data.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.full_name}</strong>
                    <span className="sub">{c.email}</span>
                  </td>
                  <td data-label={t("Telefone")}>{c.phone ?? "—"}</td>
                  <td data-label={t("Atletas")} className="right num">
                    {c.athletes_count > 0 && can("athletes.read") ? (
                      <Link to={`/org/${orgId}/atletas?treinador=${c.id}`}>{c.athletes_count}</Link>
                    ) : (
                      c.athletes_count
                    )}
                  </td>
                  <td data-label={t("Acesso")}>
                    {!c.is_active ? <Badge>{t("Desativado")}</Badge> : c.invitation_pending ? <Badge tone="warn">{t("Convite enviado")}</Badge> : <Badge tone="ok">{t("Ativo")}</Badge>}
                  </td>
                  {canManage && (
                    <td className="right" style={{ whiteSpace: "nowrap" }}>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(c)}>
                        {t("Editar")}
                      </Button>
                      <Button size="sm" variant={c.is_active ? "danger" : "secondary"} onClick={() => toggleActive(c)}>
                        {c.is_active ? t("Desativar") : t("Reativar")}
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {canManage && (
        <CoachModal
          orgId={orgId}
          coach={editing}
          onClose={() => setEditing(null)}
          onSaved={(msg) => {
            setEditing(null);
            toast(msg);
            list.reload();
          }}
        />
      )}
    </>
  );
}

function CoachModal({
  orgId,
  coach,
  onClose,
  onSaved,
}: {
  orgId: string;
  coach: Coach | "new" | null;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const isNew = coach === "new";
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", bio: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    setError(null);
    if (coach && coach !== "new") setForm({ full_name: coach.full_name, email: coach.email, phone: coach.phone ?? "", bio: coach.bio ?? "" });
    else setForm({ full_name: "", email: "", phone: "", bio: "" });
  }, [coach]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (isNew) {
        await api.post(`/orgs/${orgId}/coaches`, { ...form, phone: form.phone || null, bio: form.bio || null });
        onSaved(t("Treinador cadastrado. O convite foi enviado por e-mail."));
      } else if (coach) {
        await api.patch(`/orgs/${orgId}/coaches/${coach.id}`, { full_name: form.full_name, phone: form.phone || null, bio: form.bio || null });
        onSaved(t("Dados do treinador salvos."));
      }
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }
  const f = error?.fieldErrors() ?? {};
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  return (
    <Modal
      open={coach !== null}
      title={isNew ? t("Cadastrar treinador") : t("Editar treinador")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancelar")}
          </Button>
          <Button variant="primary" type="submit" form="coach-form" busy={busy}>
            {isNew ? t("Cadastrar e convidar") : t("Salvar alterações")}
          </Button>
        </>
      }
    >
      <form id="coach-form" className="form" onSubmit={submit} noValidate>
        {error && error.code !== "validation_error" && <FormError error={error} />}
        <TextField label={t("Nome completo")} value={form.full_name} onChange={set("full_name")} error={f.full_name} autoFocus />
        <TextField
          label={t("E-mail")}
          type="email"
          value={form.email}
          onChange={set("email")}
          error={f.email}
          disabled={!isNew}
          hint={isNew ? t("O convite de acesso vai para este endereço.") : t("O e-mail de login é alterado pelo próprio usuário.")}
        />
        <TextField label={t("Telefone")} type="tel" value={form.phone} onChange={set("phone")} error={f.phone} />
        <TextArea label={t("Apresentação")} value={form.bio} onChange={set("bio")} error={f.bio} />
      </form>
    </Modal>
  );
}
